// Alle bestillinger — the list that feeds the redesigned project page, served
// at /bestillinger (it replaced the classic table there).
//
// Same data source as the classic list had (getOrders → admin_orders/
// vendor_orders/customer orders), presented as cards in the new design
// language. Rows link to /bestillinger/order-details/:id — the V2 detail page
// took over the classic URL when the classic pages were removed.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Pagination from "@mui/material/Pagination";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import EventIcon from "@mui/icons-material/Event";
import PersonIcon from "@mui/icons-material/Person";
import InventoryIcon from "@mui/icons-material/Inventory2";
import RefreshIcon from "@mui/icons-material/Refresh";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";

import { getOrders, fetchStatusList } from "api/apiService";

const BRAND = "#006bb7";
const INK = "#111c2d";
const MUTED = "#67748e";
const LINE = "#e6ecf3";
const PAGE_BG = "#f4f7fb";

const CARD = {
  borderRadius: "16px",
  border: `1px solid ${LINE}`,
  boxShadow: "0 2px 14px rgba(16,24,40,.05)",
};

/**
 * statusVERDI → chip-farge, speiler stegene på prosjektsiden.
 *
 * Sto tidligere på delstrenger i den norske etiketten ("vent", "betaling",
 * "tilbud"). Det var dobbelt skjørt: en omdøpt etikett mistet fargen, og
 * "Avventer leveranse" traff "vent"-regelen og ble gul selv om den er planlagt.
 */
const STATUS_TONE = {
  delivered: { bg: "#e7f6ed", fg: "#0f8a43" },
  completed: { bg: "#e7f6ed", fg: "#0f8a43" },
  "sold-closed": { bg: "#e7f6ed", fg: "#0f8a43" },
  cancelled: { bg: "#fdecea", fg: "#c0392b" },
  refunded: { bg: "#fdecea", fg: "#c0392b" },
  failed: { bg: "#fdecea", fg: "#c0392b" },
  "payment-failed": { bg: "#fdecea", fg: "#c0392b" },
  "vendor-rejected": { bg: "#fdecea", fg: "#c0392b" },
  pending: { bg: "#fff4e5", fg: "#b26a00" },
  "awaiting-payment": { bg: "#fff4e5", fg: "#b26a00" },
  "on-hold": { bg: "#fff4e5", fg: "#b26a00" },
  "manual-review": { bg: "#fff4e5", fg: "#b26a00" },
};

function statusTone(statusKey) {
  const s = String(statusKey || "").toLowerCase();
  if (STATUS_TONE[s]) return STATUS_TONE[s];
  if (s === "quoting" || s === "awaiting-quote") return { bg: "#eaf2fb", fg: BRAND };
  return { bg: "#eef1f6", fg: MUTED };
}

function OrderCard({ order, onOpen }) {
  const tone = statusTone(order.status_key);
  return (
    <Card
      sx={{
        ...CARD,
        cursor: "pointer",
        transition: "all .15s ease",
        "&:hover": { borderColor: BRAND, transform: "translateY(-1px)" },
      }}
      onClick={onOpen}
    >
      <SoftBox p={2.5}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "12px",
              backgroundColor: `${BRAND}14`,
              color: BRAND,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
            }}
          >
            <ReceiptLongIcon sx={{ fontSize: 20 }} />
          </Box>

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
              <SoftTypography sx={{ fontSize: 15, fontWeight: 800, color: INK }}>
                Ordre #{order.order_id}
              </SoftTypography>
              <Chip
                size="small"
                label={order.status || "—"}
                sx={{ backgroundColor: tone.bg, color: tone.fg, fontWeight: 700 }}
              />
              {order.on_behalf && (
                <Chip
                  size="small"
                  label="På vegne av"
                  sx={{ backgroundColor: "#f3eaff", color: "#6b3fa0", fontWeight: 700 }}
                />
              )}
            </Stack>

            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap mt={0.75}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <EventIcon sx={{ fontSize: 15, color: MUTED }} />
                <SoftTypography sx={{ fontSize: 12.5, color: MUTED }}>
                  {order.date} {order.time}
                </SoftTypography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <InventoryIcon sx={{ fontSize: 15, color: MUTED }} />
                <SoftTypography sx={{ fontSize: 12.5, color: MUTED }}>
                  {order.number_of_items} leveranse{order.number_of_items === 1 ? "" : "r"}
                </SoftTypography>
              </Stack>
              {order.created_by?.name && (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <PersonIcon sx={{ fontSize: 15, color: MUTED }} />
                  <SoftTypography sx={{ fontSize: 12.5, color: MUTED }}>
                    {order.created_by.name}
                  </SoftTypography>
                </Stack>
              )}
            </Stack>
          </Box>

          <Box sx={{ textAlign: "right", flexShrink: 0 }}>
            <SoftTypography sx={{ fontSize: 10.5, color: MUTED, textTransform: "uppercase" }}>
              Total
            </SoftTypography>
            <SoftTypography sx={{ fontSize: 14, fontWeight: 700, color: INK }}>
              {order.total || "—"}
            </SoftTypography>
          </Box>

          <ChevronRightIcon sx={{ color: MUTED, flexShrink: 0 }} />
        </Stack>
      </SoftBox>
    </Card>
  );
}
OrderCard.propTypes = {
  order: PropTypes.shape({
    order_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    date: PropTypes.string,
    time: PropTypes.string,
    status: PropTypes.string,
    status_key: PropTypes.string,
    total: PropTypes.string,
    number_of_items: PropTypes.number,
    on_behalf: PropTypes.bool,
    created_by: PropTypes.shape({ name: PropTypes.string }),
  }).isRequired,
  onOpen: PropTypes.func.isRequired,
};

function ProjectTableV2() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const pageSize = 12;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = { limit: pageSize, page_number: page };
      if (statusFilter) payload.additionalData = { filterStatus: [statusFilter] };
      const res = await getOrders(payload);
      if (res?.status) {
        setOrders(Array.isArray(res.data) ? res.data : []);
        setTotalRecords(res?.data_info?.total_records ?? 0);
        setTotalPages(res?.data_info?.total_pages ?? 1);
      } else {
        setError(res?.message || "Kunne ikke hente ordrer.");
        setOrders([]);
      }
    } catch (e) {
      setError(e?.message || "Nettverksfeil.");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetchStatusList();
        // Backend svarer { status: true, status_list: [{ value, label }] }. Å lete
        // etter `data`/`results` traff aldri, så nedtrekket sto tomt og
        // statusfilteret var i praksis dødt.
        const rows = res?.status_list || res?.data || res?.results || res;
        if (Array.isArray(rows)) setStatuses(rows);
      } catch (e) {
        console.warn("fetchStatusList failed", e);
      }
    })();
  }, []);

  const summary = useMemo(() => {
    if (loading) return "Laster…";
    if (!totalRecords) return "Ingen bestillinger";
    return `${totalRecords} bestilling${totalRecords === 1 ? "" : "er"}`;
  }, [loading, totalRecords]);

  return (
    <DashboardLayout>
      <SoftBox sx={{ backgroundColor: PAGE_BG, mx: -3, px: 3, pt: 2, pb: 6, minHeight: "100vh" }}>
        <Card sx={{ ...CARD, mb: 3 }}>
          <SoftBox p={3}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              alignItems={{ xs: "flex-start", md: "center" }}
              spacing={2}
            >
              <Box sx={{ flexGrow: 1, minWidth: 220 }}>
                <SoftTypography sx={{ fontSize: 22, fontWeight: 800, color: INK }}>
                  Alle bestillinger
                </SoftTypography>
                <SoftTypography sx={{ fontSize: 13.5, color: MUTED, mt: 0.5 }}>
                  {summary}
                </SoftTypography>
              </Box>

              <Select
                displayEmpty
                value={statusFilter}
                onChange={(e) => {
                  setPage(1);
                  setStatusFilter(e.target.value);
                }}
                sx={{
                  width: { xs: "100%", md: 220 },
                  flexShrink: 0,
                  borderRadius: "12px",
                  backgroundColor: "#fff",
                  height: 42,
                  fontSize: 14,
                }}
              >
                <MenuItem value="">Alle statuser</MenuItem>
                {statuses.map((s) => (
                  <MenuItem key={s.id ?? s.value} value={s.value ?? s.id}>
                    {s.label ?? s.name ?? s.value}
                  </MenuItem>
                ))}
              </Select>

              <Tooltip title="Oppdater">
                <IconButton
                  onClick={fetchData}
                  sx={{ border: `1px solid ${LINE}`, borderRadius: "12px", backgroundColor: "#fff" }}
                >
                  <RefreshIcon sx={{ fontSize: 19, color: MUTED }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </SoftBox>
        </Card>

        {loading && (
          <SoftBox sx={{ display: "grid", placeItems: "center", py: 8 }}>
            <CircularProgress />
          </SoftBox>
        )}

        {!loading && error && (
          <Card sx={CARD}>
            <SoftBox p={3}>
              <SoftTypography sx={{ fontSize: 14, color: "#c0392b" }}>{error}</SoftTypography>
              <SoftButton sx={{ mt: 2 }} onClick={fetchData}>
                Prøv igjen
              </SoftButton>
            </SoftBox>
          </Card>
        )}

        {!loading && !error && (
          <>
            <Grid container spacing={2}>
              {orders.map((o) => (
                <Grid item xs={12} key={o.order_id}>
                  <OrderCard
                    order={o}
                    onOpen={() => navigate(`/bestillinger/order-details/${o.order_id}`)}
                  />
                </Grid>
              ))}
              {!orders.length && (
                <Grid item xs={12}>
                  <Card sx={CARD}>
                    <SoftBox p={4} sx={{ textAlign: "center" }}>
                      <SoftTypography sx={{ fontSize: 14, color: MUTED }}>
                        Ingen bestillinger å vise.
                      </SoftTypography>
                    </SoftBox>
                  </Card>
                </Grid>
              )}
            </Grid>

            {totalPages > 1 && (
              <Stack alignItems="center" mt={3}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, v) => setPage(v)}
                  color="primary"
                />
              </Stack>
            )}
          </>
        )}
      </SoftBox>
    </DashboardLayout>
  );
}

export default ProjectTableV2;
